import React, { useMemo, useState } from "react";
import {
	Pricing,
	PricingRun,
	PricingRunSummaryMetric,
	PricingStatus,
} from "../../pricingListSlice";
import {
	Box,
	Button,
	Fade,
	IconButton,
	Menu,
	MenuItem,
	SxProps,
	Theme,
	Typography,
} from "@mui/material";
import CountdownTimer from "../../../../common/components/CountdownTimer/CountdownTimer";
import { If } from "../../../../common/components/If";
import { PricingStatusComp } from "../../../../common/components/StatusIcon";
import { siteNameOrPortfolio } from "../../../../common/utils/siteNameOrPortfolio";
import { grey } from "@mui/material/colors";
import { lightGrey, primaryColor, white } from "../../../../core/theme";
import { formatPercentage } from "../../../../common/utils/formatPercentage";
import { formatDecimal } from "../../../../common/utils/formatDecimal";
import { formatMWH } from "../../../../common/utils/formatMWH";
import { formatInteger } from "../../../../common/utils/formatInteger";
import { formatCurrency } from "../../../../common/utils/formatCurrency";
import {
	formatDateDDMMYYYYCET,
	formatStringDate,
} from "../../../../common/utils/dateUtils";
import { PATH, getPathWithParameters } from "../../../../router-path";
import { Link } from "react-router-dom";
import { Column } from "../../../../common/components/ColumnsSelector/ColumnsSelector";
import * as _ from "lodash";
import { ColumnIds } from "./common";
import CommentIcon from "@mui/icons-material/Comment";
import CommentsDisabledIcon from "@mui/icons-material/CommentsDisabled";
import { notNullLike } from "../../../../common/utils/notNullLike";
import PricingTableRowBox from "../../components/PricingTable/PricingTableRowBox";
import { GenericTableRowTooltip } from "../../../../common/components/GenericTableRowTooltip";
import { dispatch } from "@engie-group/fluid-design-system-react";
import { setPersistedFilters } from "../../../filters/filtersSlice";
import { selectPersistedFilters } from "../../../filters/filters.selector";
import { useSelector } from "react-redux";
import { ClusterBreakdown } from "../../components/ClusterBreakdown";
import { formatYesNoMaybe } from "../../../../common/utils/formatYesNoMaybe";
import { getPricingType } from "../../utils/pricingTypes.utils";
import { SiteColumn } from "./siteColumn";
import { CurrencyCodeIso } from "../../../../common/constants/currency";
import { SupportedCountry } from "../../../../common/constants/countries";
import YesNoTag from "../../../../common/components/YesNoTag";
import { PricingStepperType } from "../../../pricing_request/hooks/usePricingStepper";
import LocalTime from "../../../../common/components/LocalTime/LocalTime";
import { Translation } from "react-i18next";

const style: { [key: string]: SxProps<Theme> } = {
	container: {
		minWidth: "1280px",
		bgcolor: lightGrey,
		display: "flex",
		flexDirection: "column",
		alignItems: "start",
		height: "100%",
		overflow: "hidden",
		marginBottom: "30px",
	},
	titleContainer: {
		display: "flex",
		height: 80,
		width: "100%",
		alignItems: "center",
	},
	title: {
		marginBottom: "30px",
		flexGrow: "2",
		fontWeight: 400,
		fontSize: "36px",
		margin: 0,
		display: "flex",
		alignItems: "center",
	},
	tableWrapper: {
		marginTop: "30px",
		maxWidth: "100%",
		maxHeight: "100%",
		border: `1px solid ${grey}`,
		overflow: "scroll",
	},
	table: {
		minWidth: 650,
	},
	siteOrPortfolioContainer: {
		display: "flex",
		justifyContent: "flex-start",
		alignItems: "center",
	},
	stickyRow: {
		position: "sticky",
		left: 0,
		paddingLeft: "5px !important",
		paddingTop: "10px !important",
		paddingBottom: "10px !important",
		background: white,
		"& > td": { padding: "0px" },
	},
	wayPoint: {
		height: 10,
	},
	warningIcon: {
		color: "orange",
		fontSize: 16,
	},
};

function makeCapFloorId(pricing?: Pricing): string {
	return `${pricing?.payout_method}${pricing?.payout_method_intraday}${pricing?.cap_value}${pricing?.floor_value}${pricing?.cap_value_intraday}${pricing?.floor_value_intraday}`;
}

export interface PricingCellProps {
	openGraphModal: (pricing: Pricing | undefined) => void;
	pricing?: Pricing;
	valueOrPercent?: "value" | "percent";
	pricingRun?: PricingRun;
	statusIsLoading?: boolean;
	isScrolling?: boolean;
	openWarningModal: (message: string) => void;
	getValueOrPercent: (
		metric: PricingRunSummaryMetric | undefined,
		valueFormatter: (value: number | null | undefined) => string
	) => any;
}

export interface PricingTableColumn {
	// used for key when mapping with react andas a technical id when interacting
	// with the column selector.
	columnId: ColumnIds;
	// width in pixel of this column, enforced by parent
	width: number;
	// return a unique string id by cell, used for live update
	cellIdFactory: (pricing?: Pricing, pricingRun?: PricingRun) => string;
	// return a primitive value to check if the content changed with ===
	fieldValueExtractor: (props: PricingCellProps) => any;
	// renders a component in the cell
	renderCell: (props: PricingCellProps) => React.ReactElement;
	// renders a component in the header
	renderHeader: (currency?: CurrencyCodeIso) => React.ReactElement;
}

export interface PricingTableConfiguration {
	stickyColumns: PricingTableColumn[];
	nonStickyColumns: PricingTableColumn[];
}

export const getPricingDate = (
	pricingParam: Pricing | undefined,
	pricingRunParam: PricingRun | undefined
) => {
	if (pricingParam && pricingParam?.is_real_time) {
		return "Live";
	}
	if (pricingRunParam) {
		return pricingRunParam.pricing_run_date;
	}
	if (pricingParam?.pricing_runs?.[0]?.pricing_run_date) {
		return pricingParam.pricing_runs?.[0]?.pricing_run_date;
	}
	return undefined;
};

export const getValidityDate = (
	pricingParam: Pricing | undefined,
	pricingRunParam: PricingRun | undefined
) => {
	if (pricingRunParam?.validity_date) {
		return pricingRunParam.validity_date;
	}
	if (pricingParam?.validity_date) {
		return pricingParam.validity_date;
	}
	return undefined;
};

export function simpleColumn(
	width: number,
	title: string,
	columnId: ColumnIds,
	value: (props: PricingCellProps) => any
): PricingTableColumn {
	/**
	 * Most columns' cells and header are really simple and don't really need
	 * all the props and complexity of the full PricingTableConfiguration, this helpers
	 * generate the full object from a minimal subset of properties
	 */
	return {
		width,
		columnId,
		cellIdFactory: (pricing) => "" + pricing?.id + columnId,
		fieldValueExtractor: value,
		renderCell: (props) => <>{value(props)}</>,
		renderHeader: () => <>{title}</>,
	};
}

function isBaseprod(pricing?: Pricing) {
	return pricing?.pricing_type?.is_baseprod;
}

const columns: PricingTableConfiguration = {
	stickyColumns: [
		simpleColumn(
			100,
			"Tender",
			ColumnIds.TENDER,
			({ pricing }) => pricing?.tender_name
		),
		simpleColumn(
			45,
			"GR_ID",
			ColumnIds.PGID,
			({ pricing }) => pricing?.pricing_group_id
		),
		{
			columnId: ColumnIds.PID,
			width: 40,
			cellIdFactory: (pricing) => pricing?.id.toString() || "",
			fieldValueExtractor: ({ pricing }) => pricing?.id,
			renderCell: (props) => {
				const persistedFilters = useSelector(selectPersistedFilters);
				const pricingIdParams = {
					p_id: props.pricing?.id.toString(),
				};
				const params = new URLSearchParams();
				params.set("filters", JSON.stringify(pricingIdParams));
				return (
					<Link
						to={{
							pathname: getPathWithParameters(
								PATH.TENDER_PRICING_RUN,
								{
									tenderId: props.pricing?.tender_id,
								}
							),
							search: "?" + params.toString(),
						}}
						onClick={() => {
							dispatch(
								setPersistedFilters({
									...persistedFilters.filters,
									pricings_filters_count: "1",
								})
							);
						}}
					>
						{props.pricing?.id}
					</Link>
				);
			},
			renderHeader: () => <>P_ID</>,
		},
		simpleColumn(
			45,
			"PR_ID",
			ColumnIds.PRID,
			({ pricingRun }) => pricingRun?.id
		),
		{
			columnId: ColumnIds.TOURBILLON_REF,
			width: 110,
			cellIdFactory: (pricing) => "" + pricing?.id + "tourbillon_ref",
			fieldValueExtractor: ({ pricing }) => pricing?.tourbillon_ref,
			renderCell: (props) => {
				if (props.pricing?.portfolio_tourbillon_ref_breakdown) {
					return (
						<ClusterBreakdown
							breakdown={
								props.pricing
									?.portfolio_tourbillon_ref_breakdown
							}
						/>
					);
				} else {
					const tourbillonRefValue = props.pricing?.tourbillon_ref
						? props.pricing?.tourbillon_ref
						: props.pricing?.status === "NO_PROXY"
						? ""
						: "CLUSTER";
					return (
						<PricingTableRowBox
							sx={{
								border: "0 !important",
								minWidth: 110,
								maxWidth: 110,
								width: 110,
							}}
							value={tourbillonRefValue}
						/>
					);
				}
			},
			renderHeader: () => <>Proxy</>,
		},
		{
			columnId: ColumnIds.PR_STATUS,
			width: 120,
			cellIdFactory: (pricing) => "" + pricing?.id + "pr_status",
			fieldValueExtractor: ({ pricingRun }) => pricingRun?.status,
			renderCell: ({
				pricing,
				isScrolling,
				statusIsLoading,
				pricingRun,
			}) => (
				<If condition={pricing}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							color: primaryColor,
						}}
					>
						<PricingStatusComp
							isScrolling={isScrolling}
							loader={statusIsLoading}
							pricingRun={pricingRun}
							width="100px"
						/>
						{pricing?.price_max_validity &&
							["FIXED", "BETA_FIXED"].includes(
								pricingRun?.status || ""
							) && (
								<CountdownTimer
									targetDate={
										new Date(
											pricing?.price_max_validity.endsWith(
												"Z"
											)
												? pricing?.price_max_validity
												: `${pricing?.price_max_validity}Z`
										)
									}
									size={24}
									sx={{ marginLeft: "-50px" }}
								/>
							)}
					</Box>
				</If>
			),
			renderHeader: () => <>Status</>,
		},
		{
			columnId: ColumnIds.PR_SITE,
			width: 180,
			cellIdFactory: (pricing) => "" + pricing?.id + "pr_site",
			fieldValueExtractor: ({ pricing }) => siteNameOrPortfolio(pricing),
			renderCell: ({ pricing, isScrolling, openGraphModal }) => (
				<SiteColumn
					pricing={pricing}
					isScrolling={isScrolling}
					openGraphModal={openGraphModal}
					style={style.siteOrPortfolioContainer}
				></SiteColumn>
			),
			renderHeader: () => <>Site / Port</>,
		},
		{
			columnId: ColumnIds.PR_ALPHA_OR_BETA,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "pr_alpha_or_beta",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.has_alpha || pricing?.has_beta,
			renderCell: ({ pricing }) => {
				let resultOption = pricing?.selected_result_option || "";
				return (
					<GenericTableRowTooltip
						value={resultOption}
						sx={{
							alignSelf: "center",
							textOverflow: "ellipsis",
							overflow: "hidden",
							whiteSpace: "nowrap",
							width: "100%",
						}}
					/>
				);
			},
			renderHeader: () => <>Result Option</>,
		},
		{
			columnId: ColumnIds.P_TYPE_NAME,
			width: 185,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_type_name",
			fieldValueExtractor: ({ pricing }) => getPricingType(pricing),
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={getPricingType(pricing)}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "185",
					}}
				/>
			),
			renderHeader: () => <>Pricing Type</>,
		},
		{
			columnId: ColumnIds.APPLY_SET_UP_FEES,
			width: 185,
			cellIdFactory: (pricing) =>
				"" + pricing?.id + "p_apply_set_up_fees",
			fieldValueExtractor: ({ pricing }) => {
				return pricing?.apply_set_up_fees;
			},
			renderCell: ({ pricing }) =>
				![PricingStepperType.GOO_ONLY].includes(
					pricing?.pricing_type?.family as PricingStepperType
				) ? (
					<YesNoTag
						sx={{
							alignSelf: "center",
							textOverflow: "ellipsis",
							overflow: "hidden",
							whiteSpace: "nowrap",
							width: "185",
						}}
						yes={!!pricing?.apply_set_up_fees}
					></YesNoTag>
				) : (
					<></>
				),
			renderHeader: () => <>Setup Fees Applied</>,
		},
		{
			columnId: ColumnIds.P_TECHNO_NAME,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_techno_name",
			fieldValueExtractor: ({ pricing }) => pricing?.techno?.name,
			renderCell: ({ pricing }) => (
				<PricingTableRowBox
					sx={{ width: 80 }}
					value={pricing?.techno?.name}
				/>
			),
			renderHeader: () => <>Technology</>,
		},
		{
			columnId: ColumnIds.PRICING_DATE,
			width: 85,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_pricing_date",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.pricing_runs?.[0]?.pricing_run_date,
			renderCell: ({ pricing, pricingRun }) => (
				<>{getPricingDate(pricing, pricingRun)}</>
			),
			renderHeader: () => <>Pricing Date</>,
		},
		{
			columnId: ColumnIds.P_VALIDITY_DATE,
			width: 85,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_validity_date",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.pricing_runs?.[0]?.validity_date,
			renderCell: ({ pricing, pricingRun }) => (
				<>{getValidityDate(pricing, pricingRun)}</>
			),
			renderHeader: () => <>Pricing Validity</>,
		},
		{
			columnId: ColumnIds.P_PARTY,
			width: 150,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_party",
			fieldValueExtractor: ({ pricing }) => pricing?.party_or_mirror,
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={pricing?.party_or_mirror}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				/>
			),
			renderHeader: () => <>Legal Entity / Book</>,
		},
		simpleColumn(50, "Base", ColumnIds.P_BASE_CAPA, ({ pricing }) =>
			isBaseprod(pricing)
				? formatPercentage(pricing?.base_capacity_volume_percentage)
				: "-"
		),
		{
			columnId: ColumnIds.CLICKABILITY,
			width: 50,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_clickability",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.clickability_params?.click_count,
			renderCell: ({ pricing }) => (
				<If
					condition={
						typeof pricing?.clickability_params?.click_count ===
						"number"
					}
				>
					{(pricing?.clickability_params?.click_count || -1) > 0
						? pricing?.clickability_params?.click_count
						: "No"}
				</If>
			),
			renderHeader: () => <>Click</>,
		},
		{
			columnId: ColumnIds.CLICKABILITY_GRANULARITY,
			width: 70,
			cellIdFactory: (pricing) =>
				"" + pricing?.id + "p_clickability_granularity",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.clickability_params?.granularity,
			renderCell: ({ pricing }) => (
				<If condition={pricing?.clickability_params?.granularity}>
					{pricing?.clickability_params?.granularity}
				</If>
			),
			renderHeader: () => <>Click granularity</>,
		},
		{
			columnId: ColumnIds.DELIVERY_PERIOD,
			width: 70,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_delivery_period",
			fieldValueExtractor: ({ pricing }) => pricing?.delivery_period,
			renderCell: ({ pricing }) => (
				<If condition={pricing}>{pricing?.delivery_period}</If>
			),
			renderHeader: () => <>Delivery</>,
		},
		{
			columnId: ColumnIds.P_INSTALL_CAPA,
			width: 70,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_install_capa",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.installed_capacity?.toFixed(2),
			renderCell: ({ pricing }) => (
				<>{formatDecimal(pricing?.installed_capacity)}</>
			),
			renderHeader: () => <>MW</>,
		},
		{
			columnId: ColumnIds.P_PROVISION_TYPE,
			width: 70,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_provision_type",
			fieldValueExtractor: ({ pricing }) => pricing?.provision_type,
			renderCell: ({ pricing }) => <>{pricing?.provision_type}</>,
			renderHeader: () => <>Provision type</>,
		},
		{
			columnId: ColumnIds.P_SPOT_MARKET,
			width: 70,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_spot_market",
			fieldValueExtractor: ({ pricing }) => pricing?.spot_market,
			renderCell: ({ pricing }) => <>{pricing?.spot_market}</>,
			renderHeader: () => <>Spot market</>,
		},
		{
			columnId: ColumnIds.PRICING_ADD_ONS,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				pricingRun?.id + "pricing_add_ons",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.pricing_add_ons.map((addOn) => addOn.code).join(", "),
			renderCell: ({ pricing }) => (
				<>
					{pricing?.pricing_add_ons
						.map((addOn) => addOn.code)
						.join(", ")}
				</>
			),
			renderHeader: () => <>Pricing Add Ons</>,
		},
		{
			columnId: ColumnIds.P_CHAPTER_51,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_chapter_51",
			fieldValueExtractor: ({ pricing }) => pricing?.chapter51_type,
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={`${pricing?.chapter51_type ?? ""}`}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				/>
			),
			renderHeader: () => <>§51</>,
		},
		{
			columnId: ColumnIds.P_CHAPTER_51_NUMBER_OF_HOURS,
			width: 80,
			cellIdFactory: (pricing) =>
				"" + pricing?.id + "p_chapter_51_number_of_hours",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.chapter51_number_of_hours,
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={`${pricing?.chapter51_number_of_hours ?? ""}`}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				/>
			),
			renderHeader: () => <>§51 number of hours</>,
		},
		{
			columnId: ColumnIds.NEGATIVEPRICEVALUE,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "negativeprice",
			fieldValueExtractor: ({ pricing }) => pricing?.negative_price_value,
			renderCell: ({ pricing }) => (
				<>
					<If
						condition={
							!!pricing?.negative_price_value ||
							pricing?.negative_price_value === 0
						}
					>
						{pricing?.negative_price_value}
					</If>
					<If
						condition={
							!pricing?.negative_price_value &&
							pricing?.negative_price_value !== 0
						}
					>
						-
					</If>
				</>
			),
			renderHeader: (currency) => (
				<Translation>
					{(t) => (
						<p>
							{t("pricing.list.negative_price")} ({currency})
						</p>
					)}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.NEGATIVE_PRICE_CONDITIONS,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "negativeprice",
			fieldValueExtractor: ({ pricing }) => pricing?.negative_price_value,
			renderCell: ({ pricing }) => {
				if (pricing?.has_negative_price_conditions) {
					const negativePriceConditionLabel = `${pricing?.negative_price_index?.name} ${pricing?.negative_price_operator} ${pricing?.negative_price_threshold} for ${pricing?.negative_price_nb_consecutive_blocks} consecutive blocks`;
					return (
						<GenericTableRowTooltip
							value={negativePriceConditionLabel}
							sx={{
								alignSelf: "center",
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
								width: "100%",
							}}
						/>
					);
				}
				return <>-</>;
			},
			renderHeader: () => (
				<Translation>
					{(t) => (
						<p>{t("pricing.list.negative_price_conditions")}</p>
					)}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.CURTAILABILITY_FORWARD_CONDITIONS,
			width: 80,
			cellIdFactory: (pricing) =>
				"" + pricing?.id + "curtailabilityForward",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.curtailability_pricing_index?.name,
			renderCell: ({ pricing }) => {
				if (pricing?.has_curtailability_forward_conditions) {
					const curtailabilityForwardConditionLabel = `${pricing?.curtailability_pricing_index?.name} ${pricing?.curtailability_forward_operator} ${pricing?.curtailability_forward_threshold} for ${pricing?.curtailability_price_nb_consecutive_blocks} consecutive blocks`;
					return (
						<GenericTableRowTooltip
							value={curtailabilityForwardConditionLabel}
							sx={{
								alignSelf: "center",
								textOverflow: "ellipsis",
								overflow: "hidden",
								whiteSpace: "nowrap",
								width: "100%",
							}}
						/>
					);
				}
				return <>-</>;
			},
			renderHeader: () => <>Curtailability Forward Conditions</>,
		},
		{
			columnId: ColumnIds.CAP_FLOOR,
			width: 100,
			cellIdFactory: (pricing) => "" + pricing?.id + "cap_floor",
			fieldValueExtractor: ({ pricing }) => makeCapFloorId(pricing),
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={
						<>
							<Typography variant="overline">
								Periodic: {pricing?.payout_method} [
								{pricing?.floor_value} - {pricing?.cap_value}]
							</Typography>
							<br />
							<Typography variant="overline">
								Intraday: {pricing?.payout_method_intraday} [
								{pricing?.floor_value_intraday} -{" "}
								{pricing?.cap_value_intraday}]
							</Typography>
						</>
					}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				/>
			),
			renderHeader: () => <>Cap/Floor</>,
		},
		{
			columnId: ColumnIds.SETTLEMENT_PERIOD,
			width: 100,
			cellIdFactory: (pricing) => "" + pricing?.id + "settlement_period",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.settlement_period_label,
			renderCell: ({ pricing }) => {
				return (
					<GenericTableRowTooltip
						value={pricing?.settlement_period_label}
						sx={{
							alignSelf: "center",
							textOverflow: "ellipsis",
							overflow: "hidden",
							whiteSpace: "nowrap",
							width: "100%",
						}}
					/>
				);
			},
			renderHeader: () => <>Settl. period</>,
		},
		{
			columnId: ColumnIds.BALANCING_GROUP,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "balancing_group",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.balancing_group_label,
			renderCell: ({ pricing }) => <>{pricing?.balancing_group_label}</>,
			renderHeader: () => <>Balancing Group</>,
		},
		{
			columnId: ColumnIds.MARKETING_TYPE,
			width: 80,
			cellIdFactory: (pricing) => "" + pricing?.id + "marketing_type",
			fieldValueExtractor: ({ pricing }) => pricing?.marketing_type,
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={pricing?.marketing_type}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				/>
			),
			renderHeader: () => <>Marketing type</>,
		},
		{
			columnId: ColumnIds.SHAPE_TYPE,
			width: 100,
			cellIdFactory: (pricing) => "" + pricing?.id + "shape_type",
			fieldValueExtractor: ({ pricing }) => pricing?.ppa_shape_type,
			renderCell: ({ pricing }) => (
				<GenericTableRowTooltip
					value={pricing?.ppa_shape_type}
					sx={{
						alignSelf: "center",
						textOverflow: "ellipsis",
						overflow: "hidden",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				/>
			),
			renderHeader: () => <>Shape Type</>,
		},
	],
	nonStickyColumns: [
		{
			columnId: ColumnIds.CUSTOMER_P50,
			width: 90,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_customer_p50",
			fieldValueExtractor: ({ pricing }) =>
				pricing?.site_pricings?.[0]?.site.real_power_production,
			renderCell: ({ pricing }) => (
				<>
					<If
						condition={
							pricing?.site_pricings &&
							pricing?.site_pricings.length > 1
						}
					>
						{pricing?.site_pricings.reduce(
							(t, s) => t + (s?.site.real_power_production ?? 0),
							0
						)}
					</If>

					<If
						condition={
							pricing?.site_pricings &&
							pricing?.site_pricings.length == 1
						}
					>
						{
							pricing?.site_pricings?.[0]?.site
								.real_power_production
						}
					</If>
				</>
			),
			renderHeader: () => <>Customer P50 (MWh)</>,
		},
		{
			columnId: ColumnIds.P50,
			width: 90,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_p50",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent }) =>
				getValueOrPercent(pricingRun?.summary?.P50, formatMWH),
			renderCell: ({ pricingRun, getValueOrPercent }) => (
				<>{getValueOrPercent(pricingRun?.summary?.P50, formatMWH)}</>
			),
			renderHeader: () => <>P50 (MWh)</>,
		},
		{
			columnId: ColumnIds.PROXY,
			width: 90,
			cellIdFactory: (pricing) => "" + pricing?.id + "p_proxy",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent }) =>
				getValueOrPercent(pricingRun?.summary?.Proxy, formatMWH),
			renderCell: ({ pricingRun, getValueOrPercent }) => (
				<>{getValueOrPercent(pricingRun?.summary?.Proxy, formatMWH)}</>
			),
			renderHeader: () => <>Proxy (MWh)</>,
		},
		{
			columnId: ColumnIds.LF,
			width: 90,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_lf",
			fieldValueExtractor: ({ pricingRun }) => {
				const percent = pricingRun?.summary?.LF?.percent;
				if (typeof percent === "number") {
					return parseFloat((percent * 100).toFixed(2)) + "%";
				}
				return "-";
			},
			renderCell: ({ pricingRun }) => {
				const percent = pricingRun?.summary?.LF?.percent;
				if (typeof percent === "number") {
					const roundedPercent = (percent * 100).toFixed(2) + "%";
					return <>{roundedPercent}</>;
				}
				return <>-</>;
			},
			renderHeader: () => <>LF</>,
		},
		{
			columnId: ColumnIds.QTY,
			width: 90,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_qty",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent }) =>
				getValueOrPercent(pricingRun?.summary?.Quantity, formatInteger),
			renderCell: ({ pricingRun, getValueOrPercent }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.Quantity,
						formatInteger
					)}
				</>
			),
			renderHeader: () => <>QTY (MWh)</>,
		},
		{
			columnId: ColumnIds.CROSS,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_cross",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.Cross,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.Cross,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: (currency) => <>Cross ({currency})</>,
		},
		{
			columnId: ColumnIds.GOO_ADJ_INPUT,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_goo_adj_input",
			fieldValueExtractor: ({ pricing }) =>
				formatPercentage(pricing?.goo_power_adjustment),
			renderCell: ({ pricing }) => (
				<>
					{pricing?.goo_power_adjustment === 0
						? "-"
						: formatPercentage(pricing?.goo_power_adjustment)}
				</>
			),
			renderHeader: () => (
				<Translation>
					{(t) => <p>{t("pricing.list.goo_adj_input")}</p>}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.GOO_CROSS,
			width: 90,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_goo_cross",
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.GOO_Cross?.value,
					pricing?.currency
				),
			renderCell: ({ pricingRun, pricing }) => (
				<>
					{formatCurrency(
						pricingRun?.summary?.GOO_Cross?.value,
						pricing?.currency
					)}
				</>
			),
			renderHeader: (currency) => (
				<Translation>
					{(t) => (
						<p>
							{t("pricing.list.goo_cross")} ({currency})
						</p>
					)}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.XBC,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_xbc",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.XBC,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.XBC,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>XBC</>,
		},
		{
			columnId: ColumnIds.XPC,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_xpc",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.XPC,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.XPC,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>XPC</>,
		},
		{
			columnId: ColumnIds.XDPC,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_xdpc",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.XDPC,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.XDPC,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>XDPC</>,
		},
		{
			columnId: ColumnIds.SHAPE,
			width: 100,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_shape",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.Shape,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.Shape,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Shape</>,
		},
		{
			columnId: ColumnIds.P50_PROV,
			width: 100,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_p50_prov",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.P50_PROV,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.P50_PROV,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>P50_PROV</>,
		},
		{
			columnId: ColumnIds.MARGIN_PROV,
			width: 100,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_margin_prov",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.Margin_PROV,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.Margin_PROV,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>RES_Prov</>,
		},
		{
			columnId: ColumnIds.SETUPFEE,
			width: 100,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "_setupfee",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.SetupFee,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.SetupFee,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Setup Fee</>,
		},
		{
			columnId: ColumnIds.IMBALANCECOSTPROVISION,
			width: 120,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "_imbalancecostprovision",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.ImbalanceCostProvision,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.ImbalanceCostProvision,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Imbalance Cost Provision</>,
		},
		{
			columnId: ColumnIds.PROFILECOSTPROVISION,
			width: 120,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "_profilecostprovision",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.ProfileCostProvision,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.ProfileCostProvision,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Profile Cost Provision</>,
		},
		{
			columnId: ColumnIds.DELTAPROFILECOSTPROVISION,
			width: 120,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "_deltaprofilecostprovision",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.DeltaProfileCostProvision,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.DeltaProfileCostProvision,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Delta Profile Cost Provision</>,
		},
		{
			columnId: ColumnIds.SHAPEPROVISION,
			width: 100,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "_shapeprovision",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.ShapeProvision,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.ShapeProvision,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Shape Provision</>,
		},
		{
			columnId: ColumnIds.SHAPEBIDASKPROVISION,
			width: 100,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "_shapebidaskprovision",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.ShapeBidAskProvision,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.ShapeBidAskProvision,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Shape Bid Ask Provision</>,
		},
		{
			columnId: ColumnIds.GOO_ADJ,
			width: 90,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_goo_adj",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.GOO_Adj,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.GOO_Adj,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => (
				<Translation>
					{(t) => <p>{t("pricing.list.goo_adj")}</p>}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.GOO_PRICE,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_goo",
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				pricingRun?.summary?.GOO_Price?.value
					? formatCurrency(
							pricingRun?.summary?.GOO_Price?.value,
							pricing?.currency
					  )
					: formatCurrency(
							pricingRun?.summary?.GOO?.value,
							pricing?.currency
					  ),
			renderCell: ({ pricingRun, pricing }) => (
				<>
					{pricingRun?.summary?.GOO_Price?.value
						? formatCurrency(
								pricingRun?.summary?.GOO_Price?.value,
								pricing?.currency
						  )
						: formatCurrency(
								pricingRun?.summary?.GOO?.value,
								pricing?.currency
						  )}
				</>
			),
			renderHeader: (currency) => (
				<Translation>
					{(t) => (
						<p>
							{t("pricing.list.goo_price")} ({currency})
						</p>
					)}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.MARGIN_PROVISION,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_margin_provision",
			fieldValueExtractor: ({ pricingRun }) =>
				formatPercentage(pricingRun?.summary?.Provision?.value),
			renderCell: ({ pricingRun }) => (
				<>{formatPercentage(pricingRun?.summary?.Provision?.value)}</>
			),
			renderHeader: () => <>Provision</>,
		},
		{
			columnId: ColumnIds.ALPHA_PROVISION,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_margin_provision",
			fieldValueExtractor: ({ pricingRun }) =>
				formatPercentage(pricingRun?.summary?.AlphaProvision?.value),
			renderCell: ({ pricing, pricingRun }) =>
				pricing?.has_alpha ? (
					<>
						{formatPercentage(
							pricingRun?.summary?.AlphaProvision?.value
						)}
					</>
				) : (
					<>{"-"}</>
				),
			renderHeader: () => <>Alpha Provision</>,
		},
		{
			columnId: ColumnIds.BETA_PROVISION,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_margin_provision",
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.BetaProvision?.value,
					pricing?.currency
				),
			renderCell: ({ pricing, pricingRun }) =>
				pricing?.has_beta ? (
					<>
						{formatCurrency(
							pricingRun?.summary?.BetaProvision?.value,
							pricing?.currency
						)}
					</>
				) : (
					<>{"-"}</>
				),
			renderHeader: () => <>Beta Provision</>,
		},
		{
			columnId: ColumnIds.ALPHA_BASELOAD,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_alpha",
			fieldValueExtractor: ({ pricingRun }) =>
				formatPercentage(pricingRun?.summary?.Alpha?.value, 3),
			renderCell: ({ pricingRun }) => (
				<>{formatPercentage(pricingRun?.summary?.Alpha?.value, 3)}</>
			),
			renderHeader: () => <>Alpha baseload</>,
		},
		simpleColumn(
			100,
			"Alpha Baseload Client",
			ColumnIds.PR_ALPHABASELOADCLIENT,
			({ pricingRun }) =>
				formatPercentage(pricingRun?.summary?.AlphaClient?.value)
		),
		simpleColumn(
			100,
			"Alpha Index",
			ColumnIds.ALPHAINDEX,
			({ pricing, pricingRun }) =>
				pricing?.has_alpha
					? formatPercentage(pricingRun?.summary?.AlphaIndex?.value)
					: "-"
		),
		simpleColumn(
			100,
			"Alpha Index Client",
			ColumnIds.ALPHA_INDEX_CLIENT,
			({ pricing, pricingRun }) =>
				pricing?.has_alpha
					? formatPercentage(
							pricingRun?.summary?.AlphaIndexClient?.value
					  )
					: "-"
		),
		simpleColumn(80, "Beta", ColumnIds.BETA, ({ pricing, pricingRun }) =>
			pricing?.has_beta
				? formatCurrency(
						pricingRun?.summary?.Beta?.value,
						pricing?.currency
				  )
				: "-"
		),
		simpleColumn(
			80,
			"Beta Client",
			ColumnIds.BETA_CLIENT,
			({ pricing, pricingRun }) =>
				pricing?.has_beta
					? formatCurrency(
							pricingRun?.summary?.BetaClient?.value,
							pricing?.currency
					  )
					: "-"
		),
		{
			columnId: ColumnIds.INDIC,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_indic",
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.INDIC?.value,
					pricing?.currency
				),
			renderCell: ({ pricingRun, pricing }) => (
				<>
					{formatCurrency(
						pricingRun?.summary?.INDIC?.value,
						pricing?.currency
					)}
				</>
			),
			renderHeader: () => <>Indic</>,
		},
		simpleColumn(
			80,
			"Alpha Baseload Margin",
			ColumnIds.ALPHA_BASELOAD_MARGIN,
			({ pricingRun }) =>
				formatPercentage(
					pricingRun?.summary?.AlphaBaseloadMargin?.value
				)
		),
		simpleColumn(
			80,
			"Alpha Index Margin",
			ColumnIds.ALPHA_INDEX_MARGIN,
			({ pricingRun }) =>
				formatPercentage(pricingRun?.summary?.AlphaIndexMargin?.value)
		),
		simpleColumn(
			80,
			"Beta Margin",
			ColumnIds.BETA_MARGIN,
			({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.BetaMargin?.value,
					pricing?.currency
				)
		),
		simpleColumn(
			100,
			"Indic Client",
			ColumnIds.PR_INDICCLIENT,
			({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.IndicClient?.value,
					pricing?.currency
				)
		),
		{
			columnId: ColumnIds.PR_FIXING_DT,
			width: 110,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_fixing_dt",
			fieldValueExtractor: ({ pricingRun }) =>
				formatDateDDMMYYYYCET(
					pricingRun?.summary?.FixingDateTime?.value
				),
			renderCell: ({ pricingRun }) => {
				const timezone =
					Intl.DateTimeFormat().resolvedOptions().timeZone;
				if (pricingRun?.summary?.FixingDateTime?.value) {
					return (
						<LocalTime
							utcDate={String(
								pricingRun?.summary?.FixingDateTime?.value
							)}
						>
							<Box sx={style.createdAt}>
								{formatStringDate(
									String(
										pricingRun?.summary?.FixingDateTime
											?.value
									),
									"yyyy-MM-dd, HH:mm",
									timezone
								)}
							</Box>
						</LocalTime>
					);
				}

				return <>-</>;
			},
			renderHeader: () => <>Fixing Date Time</>,
		},
		simpleColumn(
			100,
			"Baseload Price",
			ColumnIds.PR_BASELOAD_PRICE,
			({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.BaseloadPrice?.value,
					pricing?.currency
				)
		),
		simpleColumn(
			100,
			"Baseload Mid",
			ColumnIds.PR_BASELOAD_MID,
			({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.BaseloadMid?.value,
					pricing?.currency
				)
		),
		simpleColumn(
			80,
			"Fixing CM",
			ColumnIds.PR_COMMERCIAL_MARGIN,
			({ pricingRun }) => {
				if (
					pricingRun?.summary?.FixingCommercialMargin?.value ===
						null ||
					pricingRun?.summary?.FixingCommercialMargin?.value ===
						undefined
				) {
					return "-";
				}

				return (
					pricingRun?.summary?.FixingCommercialMargin?.value + " €"
				);
			}
		),
		{
			columnId: ColumnIds.FIXINGGOOCOMMERCIALMARGIN,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + ColumnIds.FIXINGGOOCOMMERCIALMARGIN,
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.FixingGooCommercialMargin?.value,
					pricing?.currency
				),
			renderCell: ({ pricingRun, pricing }) => (
				<>
					{formatCurrency(
						pricingRun?.summary?.FixingGooCommercialMargin?.value,
						pricing?.currency
					)}
				</>
			),
			renderHeader: () => (
				<Translation>
					{(t) => <p>{t("pricing.list.goo_commercial_margin")}</p>}
				</Translation>
			),
		},
		simpleColumn(
			80,
			"Fix Price",
			ColumnIds.PR_FIXPRICE,
			({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.FixPrice?.value,
					pricing?.currency
				)
		),
		{
			columnId: ColumnIds.FINALGOOPRICE,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + ColumnIds.FINALGOOPRICE,
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.FinalGooPrice?.value,
					pricing?.currency
				),
			renderCell: ({ pricingRun, pricing }) => (
				<>
					{formatCurrency(
						pricingRun?.summary?.FinalGooPrice?.value,
						pricing?.currency
					)}
				</>
			),
			renderHeader: () => (
				<Translation>
					{(t) => <p>{t("pricing.list.goo_final_price")}</p>}
				</Translation>
			),
		},
		simpleColumn(
			60,
			"Fix Beta",
			ColumnIds.PR_BETAFIX,
			({ pricing, pricingRun }) =>
				formatCurrency(
					notNullLike(
						pricing?.has_beta && pricingRun?.summary?.BetaFix?.value
					)
						? pricingRun?.summary?.BetaFix?.value
						: null,
					pricing?.currency
				)
		),
		simpleColumn(
			80,
			"Hedge Ratio",
			ColumnIds.HEDGERATIOPERCENT,
			({ pricingRun }) => {
				if (
					pricingRun?.summary?.HedgeRatioPercent?.value === null ||
					pricingRun?.summary?.HedgeRatioPercent?.value === undefined
				) {
					return "-";
				}

				return formatPercentage(
					pricingRun?.summary?.HedgeRatioPercent?.value
				);
			}
		),
		simpleColumn(
			80,
			"Total Power Margin",
			ColumnIds.TOTALPOWERMARGIN,
			({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.TotalPowerMargin?.value,
					pricing?.currency
				)
		),
		{
			columnId: ColumnIds.TOTALGOOMARGIN,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + ColumnIds.TOTALGOOMARGIN,
			fieldValueExtractor: ({ pricingRun, pricing }) =>
				formatCurrency(
					pricingRun?.summary?.TotalGOOMargin?.value,
					pricing?.currency
				),
			renderCell: ({ pricingRun, pricing }) => (
				<>
					{formatCurrency(
						pricingRun?.summary?.TotalGOOMargin?.value,
						pricing?.currency
					)}
				</>
			),
			renderHeader: () => (
				<Translation>
					{(t) => <p>{t("pricing.list.total_goo_margin")}</p>}
				</Translation>
			),
		},
		{
			columnId: ColumnIds.PR_CONTRACT_ID,
			width: 80,
			cellIdFactory: (_pricing, pricingRun) =>
				"" + pricingRun?.id + "pr_contract_id",
			fieldValueExtractor: ({ pricing }) => {
				if (pricing?.status === PricingStatus.BOOKED_MANUALLY) {
					return pricing?.geco_contract_id;
				}
				return pricing?.parties
					? pricing?.parties[0]?.geco_contract_id
					: null;
			},
			renderCell: ({ pricing }) => {
				const [contractMenu, setContractMenu] =
					useState<null | HTMLElement>(null);
				const open = Boolean(contractMenu);
				const handleClick = (event: React.MouseEvent<HTMLElement>) => {
					setContractMenu(event.currentTarget);
				};
				const handleClose = () => {
					setContractMenu(null);
				};

				return (
					<>
						<If
							condition={
								pricing?.parties &&
								pricing?.parties?.length === 1 &&
								pricing?.status === PricingStatus.BOOKED
							}
						>
							<Link
								style={{
									display: "flex",
									justifyContent: "center",
								}}
								to={
									pricing?.parties
										? getPathWithParameters(
												PATH.CONTRACT_DETAIL,
												{
													contractId:
														pricing?.parties[0]
															?.geco_contract_id,
												}
										  )
										: "#"
								}
							>
								{pricing?.parties
									? pricing?.parties[0]?.geco_contract_id
									: null}
							</Link>
						</If>
						<If
							condition={
								pricing?.parties &&
								pricing?.parties?.length === 1 &&
								pricing?.status ===
									PricingStatus.BOOKED_MANUALLY
							}
						>
							<Link
								style={{
									display: "flex",
									justifyContent: "center",
								}}
								to={
									pricing?.parties
										? getPathWithParameters(
												PATH.CONTRACT_DETAIL,
												{
													contractId:
														pricing?.geco_contract_id ??
														"",
												}
										  )
										: "#"
								}
							>
								{pricing?.geco_contract_id}
							</Link>
						</If>
						<If
							condition={
								pricing?.parties && pricing?.parties?.length > 1
							}
						>
							<Button
								id="contract-button"
								aria-controls={
									open ? "contract-menu" : undefined
								}
								aria-haspopup="true"
								aria-expanded={open ? "true" : undefined}
								onClick={handleClick}
							>
								Many
							</Button>
							<Menu
								id="contract-menu"
								MenuListProps={{
									"aria-labelledby": "contract-button",
								}}
								anchorEl={contractMenu}
								open={open}
								onClose={handleClose}
								TransitionComponent={Fade}
							>
								{pricing?.parties &&
									pricing?.parties.map((party) => (
										<MenuItem
											key={party.party_id}
											onClick={handleClose}
										>
											<Link
												to={getPathWithParameters(
													PATH.CONTRACT_DETAIL,
													{
														contractId:
															party?.geco_contract_id,
													}
												)}
											>
												{party?.geco_contract_id}
											</Link>
										</MenuItem>
									))}
							</Menu>
						</If>
					</>
				);
			},
			renderHeader: () => <>Contract Id</>,
		},
		simpleColumn(
			80,
			"Warning",
			ColumnIds.CLEANING_WARNING,
			(props: PricingCellProps) => (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
					}}
				>
					<If condition={props.pricing?.timeserie?.warning}>
						<IconButton
							aria-label="comment"
							onClick={() =>
								props.openWarningModal(
									props.pricing?.timeserie?.warning || "error"
								)
							}
						>
							<CommentIcon sx={style.warningIcon} />
						</IconButton>
					</If>
					<If condition={!props.pricing?.timeserie?.warning}>
						<CommentsDisabledIcon sx={style.warningIcon} />
					</If>
				</Box>
			)
		),
		{
			columnId: ColumnIds.IS_NODAL,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				pricingRun?.id + "_is_nodal",
			fieldValueExtractor: () => true,
			renderCell: ({ pricing }) => {
				if (
					pricing?.countries_list?.includes(
						SupportedCountry.UNITED_STATES
					)
				) {
					return <>{formatYesNoMaybe(pricing?.is_nodal)}</>;
				} else {
					return <> {"-"}</>;
				}
			},
			renderHeader: () => <>Is Nodal</>,
		},
		{
			columnId: ColumnIds.BLOCK_SHAPE,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				pricingRun?.id + "block_shape",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.BlockShape,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.BlockShape,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Block Shape</>,
		},
		{
			columnId: ColumnIds.ENERGY_BID_ASK,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				pricingRun?.id + "energy_bid_ask",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.EnergyBidAsk,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.EnergyBidAsk,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Energy Bid Ask</>,
		},
		{
			columnId: ColumnIds.PRODUCTION_VARIABILITY_BID_ASK,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				pricingRun?.id + "production_variability_bid_ask",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.ProductionVariabilityBidAsk,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.ProductionVariabilityBidAsk,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Production Variability Bid Ask</>,
		},
		{
			columnId: ColumnIds.CREDIT_COSTS,
			width: 60,
			cellIdFactory: (_pricing, pricingRun) =>
				pricingRun?.id + "credit_costs",
			fieldValueExtractor: ({ pricingRun, getValueOrPercent, pricing }) =>
				getValueOrPercent(
					pricingRun?.summary?.CreditCosts,
					(value?: number | null) =>
						formatCurrency(value, pricing?.currency)
				),
			renderCell: ({ pricingRun, getValueOrPercent, pricing }) => (
				<>
					{getValueOrPercent(
						pricingRun?.summary?.CreditCosts,
						(value?: number | null) =>
							formatCurrency(value, pricing?.currency)
					)}
				</>
			),
			renderHeader: () => <>Credit Costs</>,
		},
	],
};

export function usePricingTableRowModel(
	selectedColumns: Column<ColumnIds>[] = []
): PricingTableConfiguration {
	const filtered = useMemo(
		() => ({
			stickyColumns: columns.stickyColumns.filter(
				(column) =>
					!!_.find(
						selectedColumns,
						(selected) => selected.id === column.columnId
					)
			),
			nonStickyColumns: columns.nonStickyColumns.filter(
				(column) =>
					!!_.find(
						selectedColumns,
						(selected) => selected.id === column.columnId
					)
			),
		}),
		[selectedColumns]
	);
	return filtered;
}
